import {
  Button,
  Flex,
  Input,
  Radio,
  RadioGroup,
  Spacer,
  Stack,
  Textarea,
  VStack,
} from '@chakra-ui/react';

import { AsyncSelect, Select } from 'chakra-react-select';
import FormikFormControl from 'components/common/forms/FormikFormControl';
import ImageUploadInput from 'components/common/forms/ImageUploadInput';
import { Field, Form, Formik } from 'formik';
import costCenters from 'utils/constants/costCenters';
import { ReducerStatus } from 'utils/constants';
import PropTypes from 'prop-types';
import useFileInput from 'hooks/useFileInput';
import MultiEmailInput from 'components/common/forms/MultiEmailInput';
import { useEffect, useState } from 'react';
import ClassCoursesPreview from './ClassCoursesPreview';

function ClassForm({
  resetTrigger,
  status,
  formInputs,
  formValidation,
  onSubmitHandler,
  loadLearningPlans,
  isEditMode,
  ...rest
}) {
  const {
    file,
    fileURL,
    fileError,
    fileInputRef,
    handleAttachFile,
    handleRemoveSelectedFile,
    handleFileInputChange,
  } = useFileInput({ maxFileSizeMb: 5, fileType: 'image/' });

  // State for managing selected users/groups in MultiEmailInput
  const [selectedEmails, setSelectedEmails] = useState([]);

  // Track if we've already processed the initial emails to avoid re-processing
  const [emailsProcessed, setEmailsProcessed] = useState(false);

  /**
   * Handles adding a new item (user or group) to the selected emails list
   * @param {Object} item - The selected item with label, value, type, and data properties
   */
  const handleEmailAdd = (item) => {
    setSelectedEmails((prev) => [...prev, item]);
  };

  /**
   * Handles removing an item from the selected emails list
   * @param {Object} item - The item to remove
   */
  const handleEmailRemove = (item) => {
    setSelectedEmails((prev) =>
      prev.filter(
        (email) => email.value !== item.value || email.type !== item.type
      )
    );
  };

  useEffect(() => {
    if (resetTrigger > 0) {
      handleRemoveSelectedFile();
      setSelectedEmails([]);
      setEmailsProcessed(false); // Reset processing flag when form is reset
    }
  }, [resetTrigger, handleRemoveSelectedFile]);

  useEffect(() => {
    // Convert existing emails format to MultiEmailInput format if needed
    // Only run this conversion when formInputs.emails specifically changes
    if (
      formInputs.emails &&
      Array.isArray(formInputs.emails) &&
      formInputs.emails.length > 0
    ) {
      // Only convert if emails are strings (not already converted)
      const shouldConvert = formInputs.emails.every(
        (email) => typeof email === 'string'
      );

      if (shouldConvert) {
        const convertedEmails = formInputs.emails.map((email) => ({
          label: email,
          value: email,
          type: 'groups', // Default to groups for existing email strings
          data: { name: email },
        }));

        // Use functional update to access current selectedEmails without dependency
        setSelectedEmails((currentEmails) => {
          const currentEmailValues = currentEmails
            .map((item) => item.value)
            .sort();
          const newEmailValues = convertedEmails
            .map((item) => item.value)
            .sort();
          const areEmailsDifferent =
            JSON.stringify(currentEmailValues) !==
            JSON.stringify(newEmailValues);

          // Only update if emails are different OR if we haven't processed emails yet (initial load)
          if (areEmailsDifferent || !emailsProcessed) {
            setEmailsProcessed(true);
            return convertedEmails;
          }
          return currentEmails;
        });
      }
    } else if (
      Array.isArray(formInputs.emails) &&
      formInputs.emails.length === 0 &&
      !emailsProcessed
    ) {
      // Only clear selectedEmails during initial processing, not during user interaction
      setSelectedEmails([]);
      setEmailsProcessed(true);
    }
    // Don't do anything if formInputs.emails is null (initial loading state)
  }, [formInputs.emails, emailsProcessed]); // Remove selectedEmails from dependency to prevent circular updates

  return (
    <Formik
      enableReinitialize
      initialValues={formInputs}
      onSubmit={(values) => {
        // Convert selectedEmails back to simple email strings for compatibility
        const emails = selectedEmails.map((item) => item.value);
        onSubmitHandler({ ...values, file, emails });
      }}
      validationSchema={formValidation}
    >
      {({ values }) => (
        <Form>
          <VStack gap={5} align="start" {...rest}>
            <Field flex={1} name="name">
              {({ field, form }) => (
                <FormikFormControl
                  label="Name *"
                  helperText="Enter the name of the class. Make it concise and descriptive."
                  errors={form.errors.name}
                  form={form}
                  touched={form.touched.name}
                >
                  <Input {...field} />
                </FormikFormControl>
              )}
            </Field>

            <Field name="description">
              {({ field, form }) => (
                <FormikFormControl
                  label="Description *"
                  helperText="Provide a detailed description of the course. Mention the key objectives and what learners can expect to gain."
                  errors={form.errors.description}
                  touched={form.touched.description}
                >
                  <Textarea {...field} />
                </FormikFormControl>
              )}
            </Field>

            {isEditMode && (
              <Field name="isPublished">
                {({ field, form }) => (
                  <FormikFormControl
                    label="Published Status"
                    helperText="Choose between 'Draft' if the class is still in progress and not ready for public access, or 'Published' if the class is ready for learners."
                  >
                    <RadioGroup
                      value={field.value}
                      onChange={(e) => form.setFieldValue('isPublished', e)}
                    >
                      <Stack spacing={5} direction="row">
                        <Radio colorScheme="purple" value="false">
                          Draft
                        </Radio>
                        <Radio colorScheme="green" value="true">
                          Published
                        </Radio>
                      </Stack>
                    </RadioGroup>
                  </FormikFormControl>
                )}
              </Field>
            )}

            <ImageUploadInput
              label="Thumbnail"
              helperText="Attach a visually appealing image that represents the class. File type should be an image, and the size should not exceed 5MB."
              currentImage={formInputs.thumbnailPath}
              file={file}
              fileURL={fileURL}
              fileError={fileError}
              fileInputRef={fileInputRef}
              handleRemoveSelectedFile={handleRemoveSelectedFile}
              handleAttachFile={handleAttachFile}
              handleFileInputChange={handleFileInputChange}
            />

            <Field name="costCenter">
              {({ field, form }) => (
                <FormikFormControl
                  label="Cost Center"
                  helperText=""
                  errors={form.errors.costCenter}
                  touched={form.touched.costCenter}
                >
                  <Select
                    placeholder="Select cost center"
                    onChange={(selectedOption) => {
                      form.setFieldValue('costCenter', selectedOption?.value);
                    }}
                    options={costCenters}
                    value={costCenters.find(
                      (option) => option.value === field?.value
                    )}
                    isClearable
                  />
                </FormikFormControl>
              )}
            </Field>

            <MultiEmailInput
              label="Owner Groups"
              helperText="Search and select users or distribution groups that have the authority to modify this class. You can switch between searching users and groups using the dropdown."
              selectedItems={selectedEmails}
              isError={false}
              errorMessage=""
              onItemAdd={handleEmailAdd}
              onItemRemove={handleEmailRemove}
              defaultSearchType="groups"
            />

            <Flex gap={5} w="100%">
              <Field name="dateStart">
                {({ field, form }) => (
                  <FormikFormControl
                    flex={1}
                    label="Start Date *"
                    helperText="Enter the start date for user access to this class."
                    errors={form.errors.dateStart}
                    touched={form.touched.dateStart}
                  >
                    <Input type="date" {...field} />
                  </FormikFormControl>
                )}
              </Field>

              <Field name="dateEnd">
                {({ field, form }) => (
                  <FormikFormControl
                    flex={1}
                    label="End Date *"
                    helperText="Enter the end date to specify when user access to this class will end."
                    errors={form.errors.dateEnd}
                    touched={form.touched.dateEnd}
                  >
                    <Input type="date" {...field} />
                  </FormikFormControl>
                )}
              </Field>
            </Flex>

            <Field name="learningPlan">
              {({ form }) => (
                <FormikFormControl
                  label="Learning Plan *"
                  helperText="Please select a learning plan. A learning plan consists of a set of courses. If you don't have a learning plan yet, kindly create one first."
                  errors={form.errors.learningPlan}
                  touched={form.touched.learningPlan}
                >
                  <AsyncSelect
                    placeholder="Search learning plan"
                    onChange={(selectedOption) =>
                      form.setFieldValue('learningPlan', selectedOption)
                    }
                    value={values?.learningPlan}
                    loadOptions={loadLearningPlans}
                    isClearable
                    defaultOptions
                  />
                </FormikFormControl>
              )}
            </Field>

            <ClassCoursesPreview />

            <Flex w="100%">
              <Spacer />
              <Button
                colorScheme="red"
                px={10}
                type="submit"
                isLoading={status === ReducerStatus.LOADING}
              >
                {isEditMode ? 'Update' : 'Create'}
              </Button>
            </Flex>
          </VStack>
        </Form>
      )}
    </Formik>
  );
}

ClassForm.defaultProps = {
  isEditMode: false,
};

ClassForm.propTypes = {
  resetTrigger: PropTypes.number.isRequired,
  status: PropTypes.string.isRequired,
  formInputs: PropTypes.object.isRequired,
  formValidation: PropTypes.object.isRequired,
  onSubmitHandler: PropTypes.func.isRequired,
  loadLearningPlans: PropTypes.func.isRequired,
  isEditMode: PropTypes.bool,
};

export default ClassForm;
